import React, { useEffect, useMemo } from 'react';
import { useAimRuns } from './lib/aimQueries';
import { prettifyCategory } from './lib/categoryLabel';

function fmtRunLabel(run) {
  if (!run) return '';
  const date = run.run_date ? new Date(run.run_date + 'T00:00:00') : null;
  const dateStr = date
    ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : (run.created_at || '').slice(0, 10);
  const type = run.run_type_display || prettifyCategory(run.run_type) || 'Run';
  return `${dateStr} — ${type}`;
}

export default function RunSelector({
  clientId,
  selectedRunId,
  compareRunId,
  onChangeRun,
  onChangeCompare,
}) {
  const { data: runs, loading } = useAimRuns(clientId, 100);

  const runList = useMemo(() => Array.isArray(runs) ? runs : [], [runs]);

  // AI Monitor is run-keyed, not date-keyed: the header date-range picker is
  // disabled on this route (Epic 2), so its stored range is non-user-controllable
  // here and defaults to Last 30 Days. Filtering runs by it would silently hide
  // older runs the user cannot surface — so every completed run for the property
  // is offered (the runs endpoint already returns newest-first).
  //
  // Auto-select the latest COMPLETED run on load and whenever the property
  // changes; if the selected run is no longer in the list (e.g. a property
  // switch), fall back to the latest completed run so the dashboard never
  // renders data for a run the user can't see in the dropdown.
  useEffect(() => {
    const firstCompleted = runList.find((r) => r.status === 'completed');
    const selectedStillPresent = runList.some((r) => r.id === selectedRunId);
    if (!onChangeRun) return;
    if (!selectedRunId && firstCompleted) {
      onChangeRun(firstCompleted.id);
    } else if (selectedRunId && !selectedStillPresent) {
      onChangeRun(firstCompleted ? firstCompleted.id : '');
    }
  }, [runList, selectedRunId, onChangeRun]);

  // If the compare-to run disappears from the list (a property switch), drop it.
  useEffect(() => {
    if (!compareRunId || !onChangeCompare) return;
    const stillPresent = runList.some((r) => r.id === compareRunId);
    if (!stillPresent) onChangeCompare(null);
  }, [runList, compareRunId, onChangeCompare]);

  if (!clientId) {
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400">
        Select a client to load runs.
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-2 max-[704px]:w-full">
        <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 max-[704px]:w-24 max-[704px]:shrink-0">Run</label>
        <select
          value={selectedRunId || ''}
          onChange={(e) => onChangeRun?.(e.target.value)}
          disabled={loading || runList.length === 0}
          className="text-sm border dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent outline-none min-w-[260px] max-[704px]:!min-w-0 max-[704px]:flex-1 max-[704px]:truncate"
        >
          {runList.length === 0 && (
            <option value="">{loading ? 'Loading…' : 'No runs available'}</option>
          )}
          {runList.map((r) => {
            const incomplete = r.status !== 'completed';
            const label = incomplete ? `${fmtRunLabel(r)} (${r.status})` : fmtRunLabel(r);
            return (
              <option key={r.id} value={r.id} disabled={incomplete}>{label}</option>
            );
          })}
        </select>
      </div>
      <div className="flex items-center gap-2 w-full min-[705px]:w-auto min-[705px]:ml-2">
        <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 max-[704px]:w-24 max-[704px]:shrink-0">Compare to</label>
        <select
          value={compareRunId || ''}
          onChange={(e) => onChangeCompare?.(e.target.value || null)}
          className="text-sm border dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent outline-none min-w-[200px] max-[704px]:!min-w-0 max-[704px]:flex-1 max-[704px]:truncate"
        >
          <option value="">— None —</option>
          {runList
            .filter((r) => r.id !== selectedRunId && r.status === 'completed')
            .map((r) => (
              <option key={r.id} value={r.id}>{fmtRunLabel(r)}</option>
            ))}
        </select>
      </div>
    </div>
  );
}
